import { useEffect } from 'react';
import { Shield, Truck, Wrench, Clock, Users, BarChart3, Check } from 'lucide-react';
import { BrandHero, StatStrip, FeatureGrid, CtaBand, SectionHeader } from '@/components/brand/BrandKit';
import { FormEmbedSection } from '@/components/FormEmbedSection';
import { LEASING_FORM_ID } from '@/components/LeadConnectForm';

const HERO_IMG = "/generated/municipality-hero.jpg";

const stats = [
  { value: "1,500+", label: "Vehicles Managed" },
  { value: "24/7", label: "Operational Support" },
  { value: "98.5%", label: "Fleet Uptime" },
  { value: "Since 2006", label: "Partnership Duration" },
];

const services = [
  { icon: Truck, title: "Vehicle Leasing", desc: "Full operational lease of 1,500+ vehicles including sedans, SUVs, pickup trucks, and utility vehicles, all registered, insured, and maintained by Eurogulf Mobility Group." },
  { icon: BarChart3, title: "Fleet Management System", desc: "Web-based fleet operations software with real-time GPS tracking, live allocation, utilisation reporting, and predictive maintenance scheduling." },
  { icon: Shield, title: "RFID Card System Integration", desc: "RFID-enabled access control, vehicle pooling and car sharing system, all managed through a centralised platform." },
  { icon: Wrench, title: "Full Maintenance & Mechanical Support", desc: "Comprehensive servicing through Eurogulf Mobility Group workshops. SLA-backed turnaround with dedicated mechanics assigned to the municipality account." },
  { icon: Clock, title: "Replacement Vehicles & Roadside Assistance", desc: "Guaranteed replacement vehicles. 24/7 roadside assistance, recovery, and accident management with a single point of contact." },
  { icon: Users, title: "Car Washing & Vehicle Sterilisation", desc: "Regular car washing services and comprehensive vehicle sterilisation service for the entire managed fleet." },
];

const impact = [
  { title: "Operational Continuity", desc: "Dubai Municipality's field teams depend on reliable transport every day. Our 98.5% uptime guarantee ensures they're never off the road." },
  { title: "Cost Efficiency", desc: "By transitioning from ownership to operational leasing, the municipality reduced fleet TCO by an estimated 20%, freeing capital for core civic projects." },
  { title: "Environmental Compliance", desc: "Our fleet meets the latest UAE emissions standards and includes hybrid and electric vehicles aligned with Dubai's Clean Energy Strategy 2050." },
  { title: "Scalability", desc: "The partnership has grown from an initial fleet of 200 vehicles to over 1,500, demonstrating the model's scalability and Eurogulf Mobility Group's delivery capability." },
];

export default function DubaiMunicipalityPage() {
  useEffect(() => { document.title = "Dubai Municipality Partnership | Eurogulf Mobility Group | Fleet Management"; }, []);

  return (
    <div data-testid="dubai-municipality-page">
      <BrandHero testid="municipality-hero" chip="Flagship Corporate Partnership" title="Dubai Municipality: 1,500+ vehicles, one accountable fleet partner."
        lead="Full fleet management for one of the UAE's most important government entities. Over 1,500 vehicles leased, maintained, tracked, and managed by Eurogulf Mobility Group, supporting the operations that keep Dubai running."
        image={HERO_IMG} imageAlt="Municipal field team with managed pickup and utility vehicles in a Dubai park" position="center 60%"
        ctas={[{ label: 'Explore Corporate Fleet Solutions', href: '/corporate-rental-lease', testid: 'municipality-hero-cta' }, { label: 'Request a Quote', href: '#fleet-enquiry', variant: 'outline' }]} />
      <StatStrip stats={stats} testid="municipality-stats" />

      <FeatureGrid testid="municipality-services" chip="What We Deliver" title="A fully managed fleet ecosystem." desc="From vehicle acquisition to disposal, operating at government-grade reliability standards." items={services} />

      {/* IMPACT */}
      <section data-testid="municipality-impact" className="bg-[#FAFAFA] border-y border-black/5 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Why This Partnership Matters" title="Measurable value for a city-scale operation." align="center" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {impact.map((item) => (
              <div key={item.title} className="bg-white p-6 flex gap-4 hover:bg-[#FFF4ED]/70 transition-colors">
                <span className="mt-0.5 grid h-6 w-6 place-items-center rounded-md bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><Check className="w-3.5 h-3.5" /></span>
                <div>
                  <h3 className="font-heading font-bold text-base text-[#121212]">{item.title}</h3>
                  <p className="mt-1 font-body text-sm text-[#666666] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FormEmbedSection id="fleet-enquiry" testid="fleet-form-section" chip="Fleet Management & Leasing"
        title="Request a fleet-management or leasing quote."
        desc="Contract management, fleet leasing and customised vehicle programmes for organisations of every size."
        formId={LEASING_FORM_ID} formName="Leasing Services Form" formTestid="fleet-leasing-form" bg="bg-white" />

      <CtaBand title="Need a fleet partner at this scale?" desc="If you manage a fleet of 50 or 5,000 vehicles, Eurogulf Mobility Group has the infrastructure, technology, and experience to deliver."
        ctas={[{ label: 'Talk to Our Fleet Specialists', href: '/contact', testid: 'municipality-cta' }]} />
    </div>
  );
}
